import { Image } from '../../Image/Image';
import { Description } from '../atoms/Description/Description';
import { Headline } from '../atoms/Headline/Headline';
import { garanFont } from '../fonts';
import { GARAN_HEADLINE, GARAN_NESTED_LABEL_IMAGE_PATH } from '../garan.config';
import type { GaranDefaultProps } from '../garan.types';

/**
 * Props for the nested Garan label.
 */
export type GaranNestedLabelProps = Pick<GaranDefaultProps, 'warranty' | 'localizedDescription'> & {
  fontSizeInRem?: number;
};

/**
 * Displays the nested Garan warranty label with a localized description and warranty text.
 *
 * The component scales dynamically based on the font size.
 *
 * @param warranty - The warranty text to display (e.g., "2,5").
 * @param localizedDescription - Optional localized description for accessibility.
 * @param fontSizeInRem - Optional font size in rem units (default is 1).
 * @returns A JSX element representing the nested Garan label.
 */
export const GaranNestedLabel = ({
  warranty,
  localizedDescription,
  fontSizeInRem = 1,
}: GaranNestedLabelProps) => (
  /* needs to be a span that it can be used in a button (for triggering a dialog for example) */
  <span
    className={`garan-nested-label ${garanFont.className} leading-none text-nowrap`}
    style={{
      fontSize: `${fontSizeInRem}rem`,
    }}
  >
    <Headline />
    {localizedDescription && <Description localizedDescription={localizedDescription} />}
    {/* dynamic flex wrapper */}
    <span className="inline-flex">
      <span
        className="relative z-2 font-bold tracking-tight"
        style={{
          fontSize: '1em',
          padding: '0.1em 0',
        }}
      >
        {warranty}
      </span>
      <span>
        <Image
          src={GARAN_NESTED_LABEL_IMAGE_PATH}
          width={131}
          height={20}
          unoptimized
          alt={localizedDescription || GARAN_HEADLINE}
          className="relative z-1 block max-w-none"
          style={{
            marginLeft: '-1.35em',
            height: '1.25em',
          }}
        />
      </span>
    </span>
  </span>
);
